import React, { useCallback, useMemo, useState } from "react";
import TenderDetailsModal from "../club/TenderDetailsModal";
import ConfirmationModal from "../club/ConfirmationModal";
import PitchFilterPanel from "./PitchFilterPanel";
import PitchLifecycleHint from "./PitchLifecycleHint";
import ViewPitchesHeader from "./ViewPitchesHeader";
import ViewPitchesList from "./ViewPitchesList";
import ViewPitchesTabs from "./ViewPitchesTabs";
import { isStaffTender } from "./pitchFields";
import {
  EMPTY_FILTERS,
  applyPitchFilters,
  countActiveFilters,
} from "./pitchFilters";
import { isCompactWidth, useMeasuredWidth } from "./useMeasuredWidth";

/**
 * The View Pitches page, once. The club, staff and coach routes are three
 * entrances to this one screen — they always were near-identical copies, and
 * the only real difference is that the CLUB has the Free-Agents filter (it is
 * the club-side `free_agents_only` query the other two routes never had).
 *
 * The page measures ITS OWN column (`useMeasuredWidth`) instead of the window.
 * Every layout decision below 700px hangs off that measurement, because the
 * viewport lies: this surface lives inside an app shell, so a wide window can
 * still leave the card a phone-narrow column. `compact` is passed down rather
 * than duplicated as CSS, so a control exists exactly once in the DOM — the
 * bottom action bar and the top-right one are the same buttons, never both.
 */
const ViewPitchesScreen = ({
  tender,
  activeTab,
  setActiveTab,
  pitches,
  loading,
  error,
  showTenderDetails,
  setShowTenderDetails,
  showConfirmModal,
  setShowConfirmModal,
  confirmDialog,
  acceptSuccess = null,
  acceptSuccessDialog = null,
  dismissAcceptSuccess,
  goToAcceptedChat,
  canAcceptPitches,
  canBanAgents,
  formatPosition,
  handleAcceptReject,
  handleConfirmAction,
  handleReopen,
  handleScouting,
  handleBanAgent,
  navigate,
  freeAgentsOnly = false,
  setFreeAgentsOnly,
  showFreeAgentsFilter = false,
}) => {
  const [pageRef, pageWidth] = useMeasuredWidth();
  const compact = isCompactWidth(pageWidth);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const staffRequest = isStaffTender(tender);

  const setFilter = useCallback((key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    if (showFreeAgentsFilter && freeAgentsOnly && setFreeAgentsOnly) {
      setFreeAgentsOnly(false);
    }
  }, [showFreeAgentsFilter, freeAgentsOnly, setFreeAgentsOnly]);

  const activeFilterCount = countActiveFilters(filters, {
    staff: staffRequest,
    freeAgentsOnly: showFreeAgentsFilter && freeAgentsOnly,
  });

  const visiblePitches = useMemo(
    () => applyPitchFilters(pitches || [], filters, tender),
    [pitches, filters, tender],
  );

  return (
    <div className="min-h-screen bg-[#F5F7FB]" ref={pageRef}>
      <div className="mx-auto w-full max-w-[1160px] px-[clamp(16px,3.5vw,48px)] pb-[72px] pt-4">
        <ViewPitchesHeader
          tender={tender}
          formatPosition={formatPosition}
          navigate={navigate}
          setShowTenderDetails={setShowTenderDetails}
        />

        <ViewPitchesTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tender={tender}
        />

        <div className="mt-3 flex flex-wrap items-start gap-x-3 gap-y-2.5">
          <PitchLifecycleHint isStaffRequest={staffRequest} />
          <button
            type="button"
            onClick={() => setFiltersOpen((value) => !value)}
            aria-expanded={filtersOpen}
            className={`ml-auto inline-flex min-h-[44px] items-center gap-2 whitespace-nowrap rounded-full border px-4 text-[14px] font-semibold ${
              filtersOpen || activeFilterCount > 0
                ? "border-[#BCD8FE] bg-[#EFF6FF] text-[#1D4ED8]"
                : "border-[#E5E7EB] bg-white text-[#374151]"
            }`}
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M2 4H14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              <path d="M4.5 8H11.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              <path d="M6.5 12H9.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : "Filters"}
          </button>
        </div>

        {filtersOpen ? (
          <PitchFilterPanel
            filters={filters}
            setFilter={setFilter}
            onReset={resetFilters}
            pitches={pitches || []}
            isStaffRequest={staffRequest}
            showFreeAgentsToggle={showFreeAgentsFilter}
            freeAgentsOnly={freeAgentsOnly}
            onToggleFreeAgents={() =>
              setFreeAgentsOnly && setFreeAgentsOnly(!freeAgentsOnly)
            }
            compact={compact}
          />
        ) : null}

        <div className="mt-4">
          <ViewPitchesList
            pitches={visiblePitches}
            tender={tender}
            activeTab={activeTab}
            loading={loading}
            error={error}
            formatPosition={formatPosition}
            canAcceptPitches={canAcceptPitches}
            handleAcceptReject={handleAcceptReject}
            handleReopen={handleReopen}
            handleScouting={handleScouting}
            handleBanAgent={handleBanAgent}
            canBanAgents={canBanAgents}
            navigate={navigate}
            compact={compact}
            filtersActive={activeFilterCount > 0}
          />
        </div>
      </div>

      <TenderDetailsModal
        isOpen={showTenderDetails}
        onClose={() => setShowTenderDetails(false)}
        tender={tender}
      />

      {/* Accept, Reject and Reopen share this dialog; its copy is derived once
          in useViewPitches (`confirmDialog`) because all three role routes
          render the very same screen. */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmAction}
        variant="v2"
        {...confirmDialog}
      />

      {/* After the accept (and after a reopen, which IS an accept): the choice
          that replaced the automatic jump to the inbox. "Later" closes it and
          the club carries on down its list. */}
      <ConfirmationModal
        isOpen={!!acceptSuccess}
        onClose={dismissAcceptSuccess}
        onConfirm={goToAcceptedChat}
        variant="v2"
        {...(acceptSuccessDialog || {})}
      />
    </div>
  );
};

export default ViewPitchesScreen;
